import React, { useState, Fragment, useRef, useEffect, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Channel } from "../../../../lib/api/channels";
import { getWebSocketClient } from "../../../../lib/websocket/boardWebSocket";
import { useAuthStore } from "../../../../stores/authStore";
import { useUserChannelsStore } from "../../../../stores/userChannelsStore";
import { ChannelSelectionView, EntitySelectionView, JournalSelectionView, OperationSelectionView } from "./channel-entity";
import { useJournalStore } from "../../../../stores/journalStore";
import { BoardJournal, useBoardStore } from "../../../../stores/boardStore";

// Extended Channel interface for board usage
interface BoardChannel extends Channel {
    entities?: any[];
    isLoadingEntities?: boolean;
}

type ModalView = 'channels' | 'entities' | 'journals' | 'operations';

interface ChannelEntityModalProps {
    isOpen: boolean;
    onClose: () => void;
    onValidate: () => Promise<void>;
    onAddNewChannel: () => void;
    activeChannel: Channel | null;
    onSelectChannel: (channel: Channel) => void;
    selectedEntityIds: string[];
    onToggleEntity: (entityIds: string | string[], isSelected: boolean) => void;
    boardChannels?: BoardChannel[];
    initialView?: 'channels' | 'entities';
    boardId: string;
    linkedJournalIds?: string[];
    onToggleJournal: (journalId: string, isLinked: boolean) => Promise<void>;
    onAddJournal: () => void;
    selectedOperationIds?: string[];
    onToggleOperation?: (operationIds: string | string[], isSelected: boolean) => void;
    onValidateOperations?: (journalId?: string) => Promise<void>;
    activeJournal?: BoardJournal | null;
    onBack?: () => void;
    rootEntityId?: string;
    viewOnly?: boolean;
    boardCurrency?: string;
    currencyConversions?: Array<{ currency: string; rate: number; manual: boolean }>;
    highlightedEntityId?: string;
    shouldHighlight?: boolean;
    onClearHighlight?: () => void;
}

export const ChannelEntityModal: React.FC<ChannelEntityModalProps> = ({
    isOpen,
    onClose,
    onValidate,
    onAddNewChannel,
    activeChannel,
    activeJournal: initialActiveJournal,
    onSelectChannel,
    selectedEntityIds,
    onToggleEntity,
    boardChannels = [],
    initialView = 'channels',
    boardId,
    linkedJournalIds = [],
    onToggleJournal,
    onAddJournal,
    selectedOperationIds = [],
    onToggleOperation = () => { },
    onValidateOperations,
    onBack,
    rootEntityId,
    viewOnly = false,
    boardCurrency,
    currencyConversions,
    highlightedEntityId,
    shouldHighlight,
    onClearHighlight,
}) => {
    const token = useAuthStore(state => state.tokens?.accessToken);
    const refreshTokens = useAuthStore(state => state.refreshTokens);
    const panelRef = useRef<HTMLDivElement>(null);

    // Use global stores
    const {
        channels: userChannels,
        journals: userJournals,
        isLoading,
        loadChannels,
        updateChannel
    } = useUserChannelsStore();

    const { journals, loadJournals } = useJournalStore();
    const { finance, unfilteredFinance, operationStatuses } = useBoardStore();

    // View state
    const [currentView, setCurrentView] = useState<ModalView>(initialView);
    // ID of the entity to scope the entity tree to (e.g., a pseudo-channel's
    // source entity). When set, only that entity's descendants are rendered.
    const [scopeEntityId, setScopeEntityId] = useState<string | null>(null);
    const [activeJournal, setActiveJournal] = useState<BoardJournal | null>(null);
    const [isValidating] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [animatingWidth, setAnimatingWidth] = useState<number | null>(null);
    const wasOpenRef = useRef(false);
    const openedDirectlyToChannelRef = useRef(false);

    // Load channels and journals when modal opens
    useEffect(() => {
        if (isOpen) {
            loadChannels();
            loadJournals();
        }
    }, [isOpen, loadChannels, loadJournals]);

    // Use userJournals directly for display
    const visibleJournals = (userJournals.length > 0 ? userJournals : journals) as unknown as BoardJournal[];

    // Merge user channels with board channels data
    const channels = useMemo((): Channel[] => {
        return userChannels.map(userChannel => {
            const boardChannel = boardChannels.find(bc => bc.id === userChannel.id);
            if (boardChannel) {
                // Merge but preserve channelTypeConfig from userChannel if board copy is missing fields.
                const userConfig = userChannel.channelTypeConfig;
                const boardConfig = boardChannel.channelTypeConfig;

                const mergedChannelTypeConfig = (userConfig || boardConfig) ? {
                    name: (boardConfig?.name || userConfig?.name) ?? '',
                    icon: (boardConfig?.icon || userConfig?.icon) ?? '',
                    category: (boardConfig?.category || userConfig?.category) ?? '',
                    supportedFeatures: boardConfig?.supportedFeatures ?? userConfig?.supportedFeatures,
                    pseudoChannelEnabled: boardConfig?.pseudoChannelEnabled ?? userConfig?.pseudoChannelEnabled ?? false,
                } : undefined;

                return {
                    ...userChannel,
                    ...boardChannel,
                    channelTypeConfig: mergedChannelTypeConfig,
                    status: boardChannel.status || userChannel.status
                } as Channel;
            }
            return userChannel;
        });
    }, [userChannels, boardChannels]);

    // Reset view when modal opens
    useEffect(() => {
        const justOpened = isOpen && !wasOpenRef.current;

        if (justOpened) {
            let targetView: ModalView = 'channels';

            if (activeChannel) {
                targetView = 'entities';
            } else if (initialActiveJournal) {
                targetView = 'operations';
            } else {
                targetView = initialView || 'channels';
            }

            openedDirectlyToChannelRef.current = !!activeChannel || !!initialActiveJournal;

            setCurrentView(targetView);
            setScopeEntityId(rootEntityId || null);
            setActiveJournal(initialActiveJournal || null);
            loadChannels();
        }

        if (!isOpen) {
            openedDirectlyToChannelRef.current = false;
        }

        wasOpenRef.current = isOpen;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, highlightedEntityId, activeChannel]);

    // Calculate aggregate total value for all selected entities and operations
    const aggregateTotalValue = useMemo(() => {
        const itemTotals: Record<string, number> = {};
        const sourceData = unfilteredFinance || finance;
        if (!sourceData) return 0;

        if (sourceData.transactions && Array.isArray(sourceData.transactions)) {
            sourceData.transactions.forEach((tx: any) => {
                const entityId = String(tx.originalEntityId || tx.entityId || '');
                const operationId = tx.sourceType === 'JOURNAL' ? (tx.sourceId || tx.operationId) : null;
                const type = (tx.type || tx.transactionType || '').toUpperCase();
                const value = tx.convertedAmount || parseFloat(tx.amount || tx.value || 0);

                if (entityId && entityId !== 'unallocated') {
                    if (itemTotals[entityId] === undefined) itemTotals[entityId] = 0;
                    if (type === 'EXPENSE') itemTotals[entityId] -= value;
                    else if (type === 'REVENUE') itemTotals[entityId] += value;
                }

                if (operationId) {
                    if (itemTotals[operationId] === undefined) itemTotals[operationId] = 0;
                    if (type === 'EXPENSE') itemTotals[operationId] -= value;
                    else if (type === 'REVENUE') itemTotals[operationId] += value;
                }
            });
        }

        const entitiesTotal = selectedEntityIds.reduce((sum: number, id: string) => sum + (itemTotals[id] || 0), 0);
        const operationsTotal = (selectedOperationIds || []).reduce((sum: number, id: string) => sum + (itemTotals[id] || 0), 0);

        return entitiesTotal + operationsTotal;
    }, [unfilteredFinance, finance, selectedEntityIds, selectedOperationIds]);

    // WebSocket setup...
    useEffect(() => {
        if (!isOpen || !token) return;

        let wsClient: ReturnType<typeof getWebSocketClient> | null = null;
        const getFreshToken = async (): Promise<string> => {
            try {
                const newTokens = await refreshTokens();
                return newTokens.accessToken;
            } catch (error) {
                console.error('[ChannelEntityModal] Failed to refresh token:', error);
                throw error;
            }
        };

        try {
            wsClient = getWebSocketClient(token, getFreshToken);
            wsClient.connect().catch(error => console.error(error));

            const handleChannelSynced = (data: any) => {
                updateChannel(data.channelId, { status: 'active' as const });
                loadChannels(true);
            };

            wsClient.on('channel:synced', handleChannelSynced);
            return () => {
                if (wsClient) wsClient.off('channel:synced', handleChannelSynced);
            };
        } catch (error) {
            console.error(error);
        }
    }, [isOpen, token, refreshTokens, updateChannel, loadChannels]);


    // Helper function to safely extract entities array from channel
    const getEntitiesArray = (channel: any): any[] => {
        if (!channel) return [];
        let entities = channel.entities || [];
        // Handle case where entities is wrapped in an object with entities property
        if (!Array.isArray(entities) && typeof entities === 'object' && Array.isArray(entities.entities)) {
            entities = entities.entities;
        }
        return Array.isArray(entities) ? entities : [];
    };

    const animateViewChange = (nextView: ModalView, updateState?: () => void) => {
        if (!panelRef.current) {
            if (updateState) updateState();
            setCurrentView(nextView);
            return;
        }

        const el = panelRef.current;
        const startHeight = el.offsetHeight;
        const startWidth = el.offsetWidth;
        const targetMaxWidth = 896;

        setIsAnimating(true);
        setAnimatingWidth(startWidth);

        el.style.height = `${startHeight}px`;
        el.style.overflow = 'hidden';
        el.style.transition = 'none';
        void el.offsetHeight;

        if (updateState) updateState();
        setCurrentView(nextView);

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!panelRef.current) return;

                el.style.height = 'auto';
                void el.offsetHeight;
                const endHeight = el.scrollHeight;

                el.style.height = `${startHeight}px`;
                void el.offsetHeight;

                el.style.transition = 'max-width 300ms cubic-bezier(0.4, 0, 0.2, 1), height 300ms cubic-bezier(0.4, 0, 0.2, 1)';
                setAnimatingWidth(Math.min(targetMaxWidth, window.innerWidth - 32));
                el.style.height = `${endHeight}px`;

                setTimeout(() => {
                    if (!el) return;
                    el.style.height = '';
                    el.style.overflow = '';
                    el.style.transition = '';
                    setAnimatingWidth(null);
                    setIsAnimating(false);
                }, 320);
            });
        });
    };

    const handleChannelClick = (channel: Channel) => {
        animateViewChange('entities', () => {
            onSelectChannel(channel);
        });
    };

    const handleJournalClick = (journal: BoardJournal) => {
        animateViewChange('operations', () => {
            setActiveJournal(journal);
        });
    };

    const handleBack = () => {
        if (openedDirectlyToChannelRef.current) {
            if (onBack) {
                onBack();
            } else {
                onClose();
            }
            return;
        }

        if (currentView === 'journals' || currentView === 'operations') {
            animateViewChange('channels');
            return;
        }

        animateViewChange('channels', () => {
            setScopeEntityId(null);
            setActiveJournal(null);
        });
    };

    const getScopeEntityName = (): string | undefined => {
        if (!scopeEntityId || !activeChannel) return undefined;
        const entities = getEntitiesArray(activeChannel);
        const entity = entities.find((e: any) => e.id === scopeEntityId);
        return entity?.name;
    };

    const scopeEntityName = getScopeEntityName();

    const handleValidate = () => {
        // Start validation in background
        const validateInBackground = async () => {
            try {
                if (currentView === 'operations' && onValidateOperations) {
                    await onValidateOperations(activeJournal?.id);
                } else {
                    await onValidate();
                }
            } catch (error) {
                console.error("Error validating selection:", error);
            }
        };
        validateInBackground();

        // Immediately dismiss
        onClose();
    };

    const panelMaxWidth = animatingWidth !== null ? animatingWidth : 896;
    const panelStyle: React.CSSProperties = {
        maxWidth: Math.min(panelMaxWidth, window.innerWidth - 32),
        transition: isAnimating ? 'max-width 300ms cubic-bezier(0.4, 0, 0.2, 1)' : undefined,
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => !isValidating && onClose()} initialFocus={panelRef}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 modal-backdrop" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel
                                ref={panelRef}
                                style={panelStyle}
                                onClick={onClearHighlight}
                                className="relative bg-white dark:bg-neutral-900 border border-transparent dark:border-neutral-800 rounded-2xl shadow-2xl w-full flex flex-col overflow-hidden transform"
                            >
                                {/* Channel Selection View */}
                                {
                                    currentView === 'channels' && (
                                        <ChannelSelectionView
                                            channels={channels}
                                            journals={visibleJournals}
                                            isLoading={isLoading}
                                            isAnimating={isAnimating}
                                            onChannelClick={handleChannelClick}
                                            onJournalClick={handleJournalClick}
                                            onAddNewChannel={onAddNewChannel}
                                            onAddJournal={onAddJournal}
                                            onValidate={handleValidate}
                                            isValidating={isValidating}
                                            selectedCount={(selectedEntityIds?.length || 0) + (selectedOperationIds?.length || 0)}
                                            onClose={onClose}
                                            addedChannelIds={boardChannels.map(c => c.id)}
                                            totalValue={aggregateTotalValue}
                                            boardCurrency={boardCurrency}
                                        />
                                    )
                                }

                                {/* Entity Selection View */}
                                {
                                    currentView === 'entities' && activeChannel && (
                                        <EntitySelectionView
                                            channel={activeChannel}
                                            selectedEntityIds={selectedEntityIds}
                                            rootEntityId={scopeEntityId || undefined}
                                            bridgeEntityName={scopeEntityName}
                                            isAnimating={isAnimating}
                                            onBack={handleBack}
                                            onClose={onClose}
                                            onToggleEntity={onToggleEntity}
                                            onValidate={handleValidate}
                                            boardCurrency={boardCurrency}
                                            highlightedEntityId={highlightedEntityId}
                                            shouldHighlight={shouldHighlight}
                                        />
                                    )
                                }

                                {/* Operation Selection View */}
                                {
                                    currentView === 'operations' && activeJournal && (
                                        <OperationSelectionView
                                            journal={activeJournal}
                                            selectedOperationIds={selectedOperationIds}
                                            isAnimating={isAnimating}
                                            onBack={handleBack}
                                            onClose={onClose}
                                            onToggleOperation={(id, isSelected) => onToggleOperation(id, isSelected)}
                                            onValidate={handleValidate}
                                            viewOnly={viewOnly}
                                            boardCurrency={boardCurrency}
                                            currencyConversions={currencyConversions}
                                            operationStatuses={operationStatuses}
                                        />
                                    )
                                }

                                {/* Journal Linking View (Legacy/Management) */}
                                {
                                    currentView === 'journals' && (
                                        <JournalSelectionView
                                            boardId={boardId}
                                            linkedJournalIds={linkedJournalIds}
                                            onToggleJournal={onToggleJournal}
                                            onClose={onClose}
                                            onBack={handleBack}
                                            onAddJournal={onAddJournal}
                                        />
                                    )
                                }
                            </Dialog.Panel >
                        </Transition.Child >
                    </div >
                </div >
            </Dialog >
        </Transition >
    );
};
